import { BarChartRounded, FilterListOffRounded, IosShare } from "@mui/icons-material";
import { Button, Stack, Typography } from "@mui/material";
import { Table as TanStackTable } from "@tanstack/react-table";
import { DateTime } from "luxon";
import { useEffect, useMemo, useRef, useState } from "react";
import type { OrganizationReportRow } from "src/models";
import {
  ContainerGrid,
  HeaderLayout,
  IconLink,
  tableColumnTextFilterFn,
  TableFiltersButton,
  TeamPicker,
  useTableSettings,
  type TableColumnFilter,
} from "../../../components";
import { Paths } from "../../../constants";
import { useGetOrganizationReport } from "../../../http";
import { downloadTableAsCsv, setTitlePrefix } from "../../../utilities";
import { OrganizationReportResultsSummary, OrganizationReportTable } from "./components";
import { columnsConfig } from "./utilities";

const OrganizationReportPage = () => {
  setTitlePrefix("Organization Report");

  const { isLoading, data } = useGetOrganizationReport(true, true, true, true);
  const [selectedTeamGuids, setSelectedTeamGuids] = useState<string[]>([]);
  const [columnFilters, setColumnFilters] = useState<TableColumnFilter[]>([]);
  const tableRef = useRef<TanStackTable<OrganizationReportRow>>(null);
  const columns = useMemo(() => columnsConfig(), []);
  const { updateSettings } = useTableSettings("organization-report-table");

  // TODO: Figure out a better way to set default columnVisibility settings.
  useEffect(() => {
    updateSettings({
      columnVisibility: {
        "Team ID": false,
        "Team Slug": false,
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filteredData = useMemo(
    () =>
      data?.filter((x) => {
        let isMatch = true;

        if (selectedTeamGuids.length > 0) {
          isMatch = isMatch && selectedTeamGuids.includes(x.teamId ?? "");
        }

        if (columnFilters.length > 0) {
          isMatch = isMatch && tableColumnTextFilterFn(columns, x, columnFilters);
        }

        return isMatch;
      }),
    [columnFilters, columns, data, selectedTeamGuids],
  );

  function handleExportClicked() {
    downloadTableAsCsv({
      filename: `Organization Report - ${DateTime.now().toISODate()}`,
      table: tableRef.current,
    });
  }

  return (
    <HeaderLayout
      title={
        <Stack>
          <IconLink href={Paths.Reports} startIcon={<BarChartRounded fontSize="small" />}>
            Reports
          </IconLink>
          <Typography variant="h3">Organization Report</Typography>
          <Typography>
            Report on organizational structure, including teams, roles, people and all associated data.
          </Typography>
        </Stack>
      }
      toolbar={
        <Stack
          spacing={0.5}
          direction={{ xs: "column", md: "row" }}
          sx={{
            justifyContent: "space-between",
            width: "100%",
          }}
        >
          <Stack direction={{ xs: "column", sm: "row" }} spacing={0.5}>
            <TeamPicker
              selectedTeamGuids={selectedTeamGuids}
              onTeamSelected={(value) => setSelectedTeamGuids(value)}
              onClearClicked={() => setSelectedTeamGuids([])}
            />
            <TableFiltersButton
              table={tableRef}
              columnFilters={columnFilters}
              onColumnFiltersChanged={(columnFilters) => setColumnFilters(columnFilters)}
            />
          </Stack>
          <Stack direction="row" spacing={{ xs: 0.5, sm: 1 }}>
            <Button
              variant="tertiary"
              startIcon={<FilterListOffRounded />}
              onClick={() => {
                setSelectedTeamGuids([]);
                setColumnFilters([]);
              }}
              sx={{ width: { xs: "100%", sm: "inherit" } }}
            >
              Clear Filters
            </Button>
            <Button
              variant="tertiary"
              startIcon={<IosShare />}
              onClick={handleExportClicked}
              sx={{ width: { xs: "100%", sm: "inherit" } }}
            >
              Export CSV
            </Button>
          </Stack>
        </Stack>
      }
    >
      <ContainerGrid container spacing={{ xs: 0.5, sm: 1 }}>
        <ContainerGrid size={12}>
          <OrganizationReportResultsSummary isLoading={isLoading} data={filteredData} />
        </ContainerGrid>
        <ContainerGrid size={12}>
          <OrganizationReportTable ref={tableRef} isLoading={isLoading} data={filteredData} />
        </ContainerGrid>
      </ContainerGrid>
    </HeaderLayout>
  );
};

export { OrganizationReportPage };
